import { type KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { type IDropdownItem } from 'shared/ui'

type IUseDropdownKeyboardNavigationParams = {
  flatItems: IDropdownItem[]
  onChange?: (item: IDropdownItem, parent?: IDropdownItem) => void
  parent?: IDropdownItem
  onClose?: () => void
}

type IUseDropdownKeyboardNavigationReturn = {
  highlightedId: IDropdownItem['id'] | null
  expandedId: IDropdownItem['id'] | null
  handleKeyDown: (event: KeyboardEvent) => void
  resetHighlight: () => void
  collapseExpanded: () => void
}

const hasMultipleChildren = (item: IDropdownItem): boolean => {
  if (item.disabledChildren) return false
  if (item.customChildren) return true
  return (item.children?.length ?? 0) > 1
}

const hasAnyChildren = (item: IDropdownItem): boolean => {
  if (item.disabledChildren) return false
  if (item.customChildren) return true
  return (item.children?.length ?? 0) > 0
}

const findNextEnabledIndex = (
  items: IDropdownItem[],
  currentIndex: number,
  direction: 1 | -1
): number => {
  if (!items.length) return -1

  let index = currentIndex + direction

  for (let step = 0; step < items.length; step++) {
    if (index < 0) index = items.length - 1
    if (index >= items.length) index = 0

    if (!items[index].disabled) return index

    index += direction
  }

  return -1
}

export const useDropdownKeyboardNavigation = ({
  flatItems,
  onChange,
  parent,
  onClose,
}: IUseDropdownKeyboardNavigationParams): IUseDropdownKeyboardNavigationReturn => {
  const [highlightedId, setHighlightedId] = useState<IDropdownItem['id'] | null>(null)
  const [expandedId, setExpandedId] = useState<IDropdownItem['id'] | null>(null)

  const itemIndexById = useMemo(() => {
    const map = new Map<IDropdownItem['id'], number>()
    flatItems.forEach((item, index) => {
      map.set(item.id, index)
    })
    return map
  }, [flatItems])

  const highlightedIndex = highlightedId !== null ? itemIndexById.get(highlightedId) ?? -1 : -1

  const resetHighlight = useCallback(() => {
    setHighlightedId(null)
    setExpandedId(null)
  }, [])

  const collapseExpanded = useCallback(() => {
    setExpandedId(null)
  }, [])

  const handleSelectOrExpand = useCallback(
    (event: KeyboardEvent) => {
      if (highlightedIndex < 0 || highlightedIndex >= flatItems.length) return

      const item = flatItems[highlightedIndex]

      if (hasMultipleChildren(item)) {
        event.preventDefault()
        setExpandedId(item.id)
      } else {
        event.preventDefault()
        onChange?.(item, parent)
      }
    },
    [highlightedIndex, flatItems, onChange, parent]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          if (expandedId !== null) setExpandedId(null)
          const startIndex = highlightedIndex === -1 ? -1 : highlightedIndex
          const nextIndex = findNextEnabledIndex(flatItems, startIndex, 1)
          if (nextIndex !== -1) setHighlightedId(flatItems[nextIndex].id)
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          if (expandedId !== null) setExpandedId(null)
          const startIndex = highlightedIndex === -1 ? flatItems.length : highlightedIndex
          const prevIndex = findNextEnabledIndex(flatItems, startIndex, -1)
          if (prevIndex !== -1) setHighlightedId(flatItems[prevIndex].id)
          break
        }
        case 'ArrowRight': {
          if (highlightedIndex >= 0 && hasAnyChildren(flatItems[highlightedIndex])) {
            event.preventDefault()
            setExpandedId(flatItems[highlightedIndex].id)
          }
          break
        }
        case 'ArrowLeft': {
          if (expandedId !== null) {
            event.preventDefault()
            event.stopPropagation()
            setExpandedId(null)
          } else if (parent) {
            event.preventDefault()
            event.stopPropagation()
            onClose?.()
          }
          break
        }
        case 'Enter': {
          handleSelectOrExpand(event)
          break
        }
        case 'Escape': {
          event.preventDefault()
          event.stopPropagation()
          if (expandedId !== null) {
            setExpandedId(null)
          } else {
            onClose?.()
          }
          break
        }
        case 'Tab': {
          onClose?.()
          break
        }
      }
    },
    [highlightedIndex, expandedId, flatItems, handleSelectOrExpand, onClose]
  )

  return { highlightedId, expandedId, handleKeyDown, resetHighlight, collapseExpanded }
}
